import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../../services/tokenService';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Receipt, Search, RefreshCw, CheckCircle, Download, Eye, Printer, FolderOpen, Clock, ChevronsRight, AlertCircle, Edit, Trash2, FileSpreadsheet, ChevronLeft, ChevronRight, ChevronDown, Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { MODE_LABEL } from './constants';
import SuppressionMasseBar from './SuppressionMasseBar';
import { useAuth } from '../../contexts/AuthContext';
import { getAuthHeader } from '../../services/tokenService';
import { PaiementsAnnules } from '../ValidationVirementsESS/PaiementsAnnules';

const FILTRES = [['', 'Tous'], ['lie', 'Liés'], ['libre', 'Libres'], ['non_ref', 'Non référencés']];

const getStatutBadge = (r) => {
  const sp = r.statut_paiement;
  if (sp === 'acquitte') return <Badge className="bg-emerald-700 text-white text-[10px] font-bold border border-emerald-900"><CheckCircle className="h-3 w-3 mr-1" />Contribution acquittee</Badge>;
  if (sp === 'partiellement_paye') return (
    <div className="space-y-1">
      <Badge className="bg-amber-600 text-white text-[10px] font-bold border border-amber-800"><ChevronsRight className="h-3 w-3 mr-1" />Paiement partiel</Badge>
      {r.reste_a_payer > 0.01 && <div className="text-[10px] font-bold text-red-600">Reste: {parseFloat(r.reste_a_payer).toFixed(2)} EUR</div>}
    </div>
  );
  if (r.echeancier_actif) return <Badge className="bg-blue-700 text-white text-[10px] font-bold border border-blue-900"><Clock className="h-3 w-3 mr-1" />Echeancier actif</Badge>;
  return <Badge className="bg-orange-600 text-white text-[10px] font-bold border border-orange-800"><AlertCircle className="h-3 w-3 mr-1" />Appel a contribution</Badge>;
};

export default function RecusList({
  recus, total, page, totalPages, search, loadingList,
  expandedRecuIds, exporting,
  onSearchChange, typeRecu, onTypeChange, onPageChange, onToggleExpand, onAction,
  onRegenerate, onEdit, onEcheancier, onExport,
  selected, setSelected, onBulkDone,
}) {
  const { user } = useAuth();
  const superAdmin = (user?.role || '').toLowerCase() === 'super_admin';
  const onDeletePaiement = async (r, p) => {
    if (!window.confirm(`Retirer le paiement de ${parseFloat(p.montant || 0).toFixed(2)} EUR${p.date ? ` du ${new Date(p.date).toLocaleDateString('fr-FR')}` : ''} ?\n\nIl sera retiré du reçu acquitté (régénéré) et restera restaurable dans « Paiements annulés ».`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/souscriptions/admin/${r.souscription_id}/partial-payment/${p.id}`, { headers: getAuthHeader() });
      toast.success('Paiement retiré — reçu acquitté régénéré');
      onBulkDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Retrait impossible'); }
  };
  const onDeleteEcheance = async (r, ech) => {
    if (ech.statut === 'paye') { toast.error("Échéance déjà payée : annulez d'abord le paiement avant de la supprimer"); return; }
    if (!window.confirm(`Supprimer l'échéance n°${ech.numero} (${parseFloat(ech.montant || 0).toFixed(2)} EUR) du reçu ${r.numero_recu} ?\n\nLe reçu sera régénéré. Vous pourrez la restaurer depuis le panneau Échéancier.`)) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/recus-ess/${r.id}/echeancier/${ech.id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Échéance supprimée — reçu régénéré');
      onBulkDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Suppression impossible'); }
  };
  return (
    <>
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input type="text" value={search} onChange={e => onSearchChange(e.target.value)} placeholder="N° reçu, bénéficiaire, n° souscription, réf. virement..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500" data-testid="recu-list-search" />
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="px-3 py-1.5"><Receipt className="h-3.5 w-3.5 mr-1.5" /> {total} Recu{total > 1 ? 's' : ''} ESS</Badge>
          <Button variant="outline" size="sm" onClick={() => onExport('csv')} disabled={exporting} className="text-xs" data-testid="btn-export-csv">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => onExport('excel')} disabled={exporting} className="text-xs" data-testid="btn-export-excel">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-1">
        <span className="text-sm text-gray-500">{total} recu(s) ESS</span>
        <div className="flex gap-1" data-testid="recu-filtre-type">
          {FILTRES.map(([v, l]) => (
            <button key={v || 'tous'} type="button" onClick={() => onTypeChange(v)}
              className={cn("px-3 py-1 rounded-full text-xs font-medium border transition-colors", typeRecu === v ? "bg-orange-600 text-white border-orange-600" : "bg-white text-gray-600 border-gray-200 hover:border-orange-300")}
              data-testid={`recu-filtre-${v || 'tous'}`}>{l}</button>
          ))}
        </div>
      </div>
      <SuppressionMasseBar recus={recus} selected={selected} setSelected={setSelected} onDone={onBulkDone} />

      {loadingList ? (
        <div className="flex items-center justify-center py-16"><RefreshCw className="h-8 w-8 animate-spin text-orange-400" /></div>
      ) : recus.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <Receipt className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <p className="font-medium text-gray-700">Aucun Recu ESS manuel</p>
          <p className="text-sm mt-1 text-gray-400">Cliquez sur &quot;Nouveau Recu ESS&quot; pour commencer</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="recus-ess-list">
          {recus.map((r) => {
            const isExpanded = expandedRecuIds.has(r.id);
            const sp = r.statut_paiement;
            const borderColor = sp === 'acquitte' ? 'border-emerald-200' : sp === 'partiellement_paye' ? 'border-amber-200' : 'border-orange-200';
            const dateStr = r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

            return (
              <div
                key={r.id}
                className={cn(
                  "rounded-lg border transition-all duration-150",
                  isExpanded ? `${borderColor} shadow-sm bg-white` : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                )}
                data-testid={`recu-row-${r.id}`}
              >
                <div className="flex items-center">
                <input type="checkbox" checked={selected.has(r.id)} onClick={e => e.stopPropagation()}
                  onChange={() => setSelected(prev => { const n = new Set(prev); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })}
                  className="ml-4 h-4 w-4 shrink-0 accent-orange-500 cursor-pointer" data-testid={`recu-select-${r.id}`} />
                <button onClick={() => onToggleExpand(r.id)} className="flex items-center gap-3 px-4 py-2.5 w-full text-left" data-testid={`recu-toggle-${r.id}`}>
                  <Receipt className={cn("h-4 w-4 shrink-0", sp === 'acquitte' ? 'text-emerald-500' : sp === 'partiellement_paye' ? 'text-amber-500' : 'text-orange-500')} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("font-medium text-sm truncate", isExpanded ? "text-orange-700" : "text-gray-900")}>{r.raison_sociale || 'N/A'}</p>
                    <p className="text-[10px] text-gray-400 font-mono truncate">{r.numero_recu}</p>
                  </div>
                  {getStatutBadge(r)}
                  {r.is_libre
                    ? <Badge className="bg-orange-50 text-orange-600 text-[9px] font-medium border border-orange-200 shrink-0 hidden sm:inline-flex">Libre</Badge>
                    : <Badge className="bg-blue-50 text-blue-600 text-[9px] font-medium border border-blue-200 shrink-0 hidden sm:inline-flex">Lie</Badge>
                  }
                  <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                    <Calendar className="h-3 w-3" /><span>{dateStr}</span>
                  </div>
                  <span className="text-sm font-bold text-orange-700 tabular-nums shrink-0">{r.montant_ttc ? parseFloat(r.montant_ttc).toFixed(2) : '0.00'} EUR</span>
                  {r.reste_a_payer > 0.01 && <span className="text-[10px] font-bold text-red-600 shrink-0 hidden lg:inline">Reste: {parseFloat(r.reste_a_payer).toFixed(2)}</span>}
                  <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-orange-500")} />
                </button>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`recu-details-${r.id}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Col 1: Beneficiaire */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Beneficiaire</h4>
                        <div className="space-y-1.5 text-sm">
                          <p className="font-medium text-gray-900">{r.raison_sociale || 'N/A'}</p>
                          {r.sous_type_dispositif && <p className="text-indigo-600 text-xs font-medium">{r.sous_type_dispositif}</p>}
                          {r.type_dispositif && <p className="text-gray-500 text-xs">{r.type_dispositif}</p>}
                          {r.numero_police && <p className="text-gray-500 text-xs font-mono" data-testid={`recu-num-souscription-${r.id}`}>Souscription : {r.numero_police}</p>}
                          {r.reference_paiement && <p className="text-gray-500 text-xs truncate">Réf. : {r.reference_paiement}</p>}
                          <div className="flex items-center gap-1.5 text-gray-600">
                            <Badge variant="outline" className="text-[10px] capitalize">{MODE_LABEL(r.mode_paiement)}</Badge>
                          </div>
                          <div className="text-gray-600 text-xs">
                            GED: {r.ged_classee ? <Badge className="bg-emerald-700 text-white text-[10px] font-semibold"><CheckCircle className="h-3 w-3 mr-0.5" />Oui</Badge> : <Badge variant="outline" className="text-[10px] text-gray-400">Non</Badge>}
                          </div>
                        </div>
                      </div>

                      {/* Col 2: Financier */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Financier</h4>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Montant Global:</span>
                            <span className="font-bold text-orange-700">{r.montant_ttc ? parseFloat(r.montant_ttc).toFixed(2) : '0.00'} EUR</span>
                          </div>
                          {r.montant_paye > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Paye:</span>
                              <span className="font-bold text-emerald-600">{parseFloat(r.montant_paye).toFixed(2)} EUR</span>
                            </div>
                          )}
                          {r.reste_a_payer > 0.01 && (
                            <div className="flex justify-between">
                              <span className="text-gray-500">Reste:</span>
                              <span className="font-bold text-red-600">{parseFloat(r.reste_a_payer).toFixed(2)} EUR</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-500">Date:</span>
                            <span className="text-gray-700">{dateStr}</span>
                          </div>
                        </div>
                        {r.echeances && r.echeances.length > 0 && (
                          <div className="bg-orange-50 rounded-lg p-2 border border-orange-200 mt-1">
                            <p className="text-[10px] font-semibold text-orange-700 mb-1">Echeancier ({r.echeances.length})</p>
                            <div className="space-y-1 max-h-28 overflow-y-auto">
                              {r.echeances.map((ech, i) => (
                                <div key={i} className="flex items-center gap-1 text-[10px]">
                                  <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", ech.statut === 'paye' ? 'bg-emerald-400' : ech.statut === 'en_retard' ? 'bg-red-400' : 'bg-amber-400')} />
                                  <span className="font-medium">#{ech.numero}</span>
                                  <span className={ech.statut === 'paye' ? 'text-emerald-600' : 'text-gray-600'}>{parseFloat(ech.montant || 0).toFixed(2)} EUR</span>
                                  {ech.statut === 'paye' && <Badge className="bg-emerald-700 text-white text-[9px] font-semibold">PAYE</Badge>}
                                  {ech.statut === 'en_retard' && <Badge className="bg-red-700 text-white text-[9px] font-semibold">RETARD</Badge>}
                                  {ech.id && (
                                    <button type="button" onClick={(e) => { e.stopPropagation(); onDeleteEcheance(r, ech); }}
                                      className="ml-auto p-0.5 rounded hover:bg-red-100 text-red-600" title="Supprimer cette échéance"
                                      data-testid={`recu-echeance-delete-${ech.id}`}>
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(!r.echeances || r.echeances.length === 0) && r.paiements && r.paiements.length > 0 && (
                          <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-200 mt-1">
                            <p className="text-[10px] font-semibold text-emerald-700 mb-1">Paiements ({r.paiements.length})</p>
                            <div className="space-y-1">
                              {r.paiements.map((p, i) => (
                                <div key={i} className="flex items-center gap-1 text-[10px] text-emerald-600">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                                  <span>{parseFloat(p.montant).toFixed(2)} EUR</span>
                                  <span className="text-gray-400">({p.mode || p.type})</span>
                                  {superAdmin && p.id && r.souscription_id && (
                                    <button type="button" onClick={(e) => { e.stopPropagation(); onDeletePaiement(r, p); }}
                                      className="ml-auto p-0.5 rounded hover:bg-red-100 text-red-600" title="Retirer ce paiement"
                                      data-testid={`recu-paiement-delete-${r.id}-${p.id}`}>
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {r.souscription_id && r.nb_paiements_annules > 0 && (
                          <PaiementsAnnules doc={{ id: r.souscription_id, paiements_annules: Array(r.nb_paiements_annules).fill(0), total_paye: r.montant_paye }}
                            getAuthHeader={getAuthHeader} onDone={onBulkDone} />
                        )}
                      </div>

                      {/* Col 3: Actions */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>
                        <div className="flex flex-wrap gap-1.5">
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onAction('view', r); }} className="h-8 text-xs gap-1.5" data-testid={`btn-view-${r.id}`}>
                            <Eye className="h-3.5 w-3.5" /> Visualiser
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onAction('download', r); }} className="h-8 text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`btn-download-${r.id}`}>
                            <Download className="h-3.5 w-3.5" /> Telecharger
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onAction('print', r); }} className="h-8 text-xs gap-1.5" data-testid={`btn-print-${r.id}`}>
                            <Printer className="h-3.5 w-3.5" /> Imprimer
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onRegenerate(r); }} className="h-8 text-xs gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid={`btn-regen-${r.id}`}>
                            <RefreshCw className="h-3.5 w-3.5" /> Regenerer
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(r); }} className="h-8 text-xs gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50" data-testid={`btn-edit-${r.id}`}>
                            <Edit className="h-3.5 w-3.5" /> Configurer
                          </Button>
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onEcheancier(r); }} className="h-8 text-xs gap-1.5 border-orange-200 text-orange-700 hover:bg-orange-50" data-testid={`btn-echeancier-${r.id}`}>
                            <Clock className="h-3.5 w-3.5" /> Echeancier
                          </Button>
                          {!r.ged_classee && (
                            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onAction('ged', r); }} className="h-8 text-xs gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                              <FolderOpen className="h-3.5 w-3.5" /> Classer GED
                            </Button>
                          )}
                          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onAction('delete', r); }} className="h-8 text-xs gap-1.5 border-red-200 text-red-700 hover:bg-red-50">
                            <Trash2 className="h-3.5 w-3.5" /> Supprimer
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">Page {page}/{totalPages} ({total} resultats)</p>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPageChange(page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </>
  );
}
